import { z } from 'zod';

/**
 * SIL-575 (I2-D3) — Digital Passports registry, Intelligence › Outcomes &
 * Reports. Fleet-wide read model over the SAME data the per-asset print
 * surface (passport.$fingerprint.tsx, telemetryPassportClientResponseSchema
 * in ./telemetry-passport.ts) already assembles — no new passport content, no
 * migrations. One row per org asset with real-data-derived completeness
 * (which §-sections of that document currently have data), so a fleet
 * manager can see AT A GLANCE which assets have a thin passport before a
 * buyer ever opens one.
 *
 * `sections` intentionally collapses the passport's five §-sections onto
 * four real presence checks (dropping the always-true §1 Identity — every
 * row here already resolved from a real asset match, so counting it would
 * pad every row's completeness without signaling anything):
 *   - `health`        → telemetry_score_snapshot exists for the asset (§2)
 *   - `reliability`    → telemetry_daily_agg has any rows (§3 damage budget +
 *                         §4 disclosures both derive from this table in
 *                         assemblePassportForAsset — see that file's own
 *                         doc comment for the exact derivation this mirrors)
 *   - `fmv`            → asset.latest_pricing_snapshot_id resolves (v_asset_list's
 *                         latest_fmv_med_usd is non-null) — registry callers are
 *                         always the owning seller org, so FMV is never masked
 *                         the way a buyer-viewed passport masks it
 *   - `environmental`  → v_asset_carbon_footprint_latest has a COMPLETE row
 *                         (same required-field check assemblePassportForAsset
 *                         uses to decide carbonFootprint vs null)
 */
export const PASSPORT_REGISTRY_SECTIONS = ['health', 'reliability', 'fmv', 'environmental'] as const;
export type PassportRegistrySection = (typeof PASSPORT_REGISTRY_SECTIONS)[number];

export const PASSPORT_REGISTRY_SECTION_LABELS: Record<PassportRegistrySection, string> = {
  health: 'Health & operational fitness',
  reliability: 'Reliability & disclosures',
  fmv: 'Fair market value',
  environmental: 'Environmental — embodied carbon',
};

export const passportRegistryEntrySchema = z.object({
  assetId: z.string().uuid(),
  /** Public passport handle, `<prefix>-<token>` (formatFingerprint) - the passport URL key. */
  fingerprint: z.string(),
  serialNumber: z.string().nullable(),
  assetTag: z.string().nullable(),
  assetClassName: z.string().nullable(),
  manufacturerName: z.string().nullable(),
  skuModelName: z.string().nullable(),
  lifecycleStateName: z.string().nullable(),
  /** Which of PASSPORT_REGISTRY_SECTIONS currently have real data behind them. */
  sections: z.array(z.enum(PASSPORT_REGISTRY_SECTIONS)),
  /** `sections.length` / PASSPORT_REGISTRY_SECTIONS.length, precomputed for display. */
  completeness: z.object({
    populated: z.number().int().min(0),
    total: z.number().int().min(0),
  }),
  /** Latest of asset.updated_at, the health score's scored_at, the FMV
   * snapshot's priced_at, and the carbon footprint's computed_at — whichever
   * of the asset's underlying passport-feeding records changed most recently. */
  lastUpdated: z.string().nullable(),
});
export type PassportRegistryEntry = z.infer<typeof passportRegistryEntrySchema>;

export const passportRegistryResponseSchema = z.object({
  data: z.array(passportRegistryEntrySchema),
});
export type PassportRegistryResponse = z.infer<typeof passportRegistryResponseSchema>;
